import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Typography, useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { deepPurple } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import type {
  AnnualRevenueRange as AnnualRevenueRangeType,
  BusinessStage as BusinessStageType,
  IBusinessProfile,
  UpdateBusinessProfileInput,
  WorkforceStructure as WorkforceStructureType,
} from '@optimus/models';
import {
  AnnualRevenueRange,
  BusinessStage,
  WorkforceStructure,
} from '../../common/enums';
import { QuestionOption } from '../../common/models/Question';
import { QuestionComponentAutocomplete } from '../../common/QuestionComponentParts/QuestionComponentAutocomplete';
import { TestIds } from '../../assets/testIds';

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

interface BusinessProfileProps {
  profile: IBusinessProfile;
  tradeOptions: QuestionOption[];
  onSave: (input: UpdateBusinessProfileInput) => Promise<void>;
  onFinishLater: () => void;
  onReviewed: () => void;
}

const getFirstIncompleteStep = (profile: IBusinessProfile) => {
  if (!profile.primaryTrade) return 0;
  if (!profile.workforceStructure) return 1;
  if (!profile.businessStage) return 2;
  if (!profile.annualRevenueRange) return 3;
  return 4;
};

export const getBusinessProfileCompletion = (profile: IBusinessProfile | null) => {
  if (!profile) return 0;
  return [
    profile.primaryTrade,
    profile.annualRevenueRange,
    profile.workforceStructure,
    profile.businessStage,
  ].filter(Boolean).length;
};

// The profile is put back in front of the member every three months so the answers feeding the AI
// Coach stay current. `updatedAt` records the member's last interaction with the profile — an
// answer changing, or the banner being acted on — and each interaction restarts the three months.
// A member who has never interacted has no `updatedAt`, so the reminder is due straight away and
// stays up until they engage with it.
export const isBusinessProfileReminderDue = (profile: IBusinessProfile | null) => {
  if (!profile) return false;
  if (!profile.updatedAt) return true;
  return Date.now() - new Date(profile.updatedAt).getTime() >= NINETY_DAYS_MS;
};

function ChoiceButton({
  selected, children, onClick, testId,
}: {
  selected: boolean;
  children: React.ReactNode;
  onClick: () => void;
  testId: string;
}) {
  const theme = useTheme();
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      data-testid={testId}
      sx={{
        minHeight: theme.spacing(7.5),
        justifyContent: 'flex-start',
        textAlign: 'left',
        borderRadius: theme.spacing(1.5),
        borderColor: selected ?
          deepPurple[500] : theme.palette.grey[300],
        color: selected ? deepPurple[500] : theme.palette.grey[800],
        backgroundColor: selected ?
          deepPurple[50] : theme.palette.common.white,
        '&:hover': {
          borderColor: deepPurple[500],
          backgroundColor: deepPurple[50],
        },
      }}
    >
      {children}
    </Button>
  );
}

export function BusinessProfile({
  profile, tradeOptions, onSave, onFinishLater, onReviewed,
}: BusinessProfileProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [step, setStep] = useState(getFirstIncompleteStep(profile));
  const [returnStep, setReturnStep] = useState<number | null>(null);
  const [selectedTrade, setSelectedTrade] = useState(profile.primaryTrade ?? '');
  const [revenue, setRevenue] = useState<AnnualRevenueRangeType | null>(
    profile.annualRevenueRange,
  );
  const [workforce, setWorkforce] = useState<WorkforceStructureType | null>(
    profile.workforceStructure,
  );
  const [fieldCount, setFieldCount] = useState(profile.fieldEmployeeCount ?? 0);
  const [officeCount, setOfficeCount] = useState(profile.officeEmployeeCount ?? 0);
  const [stage, setStage] = useState<BusinessStageType | null>(profile.businessStage);
  const [saving, setSaving] = useState(false);
  const [changedSinceEdit, setChangedSinceEdit] = useState(true);
  const editFormRef = useRef<HTMLDivElement>(null);
  const prevStepRef = useRef<number>(step);

  useEffect(() => {
    const wasStep4 = prevStepRef.current === 4 && step !== 4;
    if (wasStep4 || returnStep !== null) {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevStepRef.current = step;
  }, [step, returnStep]);

  // A trade the member already has stays selectable even if the form's list does not offer it.
  const tradePickerOptions = selectedTrade && !tradeOptions.some(
    (option) => option.value === selectedTrade,
  ) ? [{ value: selectedTrade, display: selectedTrade }, ...tradeOptions] : tradeOptions;
  const selectedTradeOption = tradePickerOptions.find(
    (option) => option.value === selectedTrade,
  ) ?? null;

  const revenueOptions = [
    {
      value: AnnualRevenueRange.Under100K,
      label: t('coach.businessProfile.revenue.under100K'),
    },
    {
      value: AnnualRevenueRange.Between100KAnd250K,
      label: t('coach.businessProfile.revenue.between100KAnd250K'),
    },
    {
      value: AnnualRevenueRange.Between250KAnd500K,
      label: t('coach.businessProfile.revenue.between250KAnd500K'),
    },
    {
      value: AnnualRevenueRange.Between500KAnd1M,
      label: t('coach.businessProfile.revenue.between500KAnd1M'),
    },
    {
      value: AnnualRevenueRange.Between1MAnd5M,
      label: t('coach.businessProfile.revenue.between1MAnd5M'),
    },
    {
      value: AnnualRevenueRange.Over5M,
      label: t('coach.businessProfile.revenue.over5M'),
    },
  ];

  const workforceOptions = [
    {
      value: WorkforceStructure.SoleOperator,
      title: t('coach.businessProfile.workforce.soleOperator.title'),
      detail: t('coach.businessProfile.workforce.soleOperator.detail'),
      icon: <PersonOutlineIcon />,
    },
    {
      value: WorkforceStructure.FamilyBusiness,
      title: t('coach.businessProfile.workforce.familyBusiness.title'),
      detail: t('coach.businessProfile.workforce.familyBusiness.detail'),
      icon: <PeopleOutlineIcon />,
    },
    {
      value: WorkforceStructure.SmallCrew,
      title: t('coach.businessProfile.workforce.smallCrew.title'),
      detail: t('coach.businessProfile.workforce.smallCrew.detail'),
      icon: <HomeRepairServiceOutlinedIcon />,
    },
    {
      value: WorkforceStructure.EstablishedTeam,
      title: t('coach.businessProfile.workforce.establishedTeam.title'),
      detail: t('coach.businessProfile.workforce.establishedTeam.detail'),
      icon: <PeopleOutlineIcon />,
    },
  ];

  const stageOptions = [
    {
      value: BusinessStage.Foundation,
      title: t('coach.businessProfile.stage.foundation.title'),
      detail: t('coach.businessProfile.stage.foundation.detail'),
    },
    {
      value: BusinessStage.Growth,
      title: t('coach.businessProfile.stage.growth.title'),
      detail: t('coach.businessProfile.stage.growth.detail'),
    },
    {
      value: BusinessStage.Scaling,
      title: t('coach.businessProfile.stage.scaling.title'),
      detail: t('coach.businessProfile.stage.scaling.detail'),
    },
    {
      value: BusinessStage.Mature,
      title: t('coach.businessProfile.stage.mature.title'),
      detail: t('coach.businessProfile.stage.mature.detail'),
    },
  ];

  const workforceTitle = workforceOptions.find(
    (option) => option.value === profile.workforceStructure,
  )?.title;
  const stepDefs = [
    {
      label: t('coach.businessProfile.trade.heading'),
      value: profile.primaryTrade,
      step: 0,
    },
    {
      label: t('coach.businessProfile.workforce.heading'),
      value: workforceTitle ? t('coach.businessProfile.summary.workforceValue', {
        title: workforceTitle,
        fieldCount: profile.fieldEmployeeCount ?? 0,
        officeCount: profile.officeEmployeeCount ?? 0,
      }) : '',
      step: 1,
    },
    {
      label: t('coach.businessProfile.stage.heading'),
      value: stageOptions.find(
        (option) => option.value === profile.businessStage,
      )?.detail ?? '',
      step: 2,
    },
    {
      label: t('coach.businessProfile.revenue.heading'),
      value: revenueOptions.find(
        (option) => option.value === profile.annualRevenueRange,
      )?.label ?? '',
      step: 3,
    },
  ];

  // Only answered steps — used above the current form and on the step-4 review screen
  const summaries = stepDefs.filter((summary) => summary.value && summary.step !== step);

  const save = async (input: UpdateBusinessProfileInput, nextStep: number) => {
    const merged = { ...profile, ...input };
    const allAnswered = !!(
      merged.primaryTrade && merged.workforceStructure &&
      merged.businessStage && merged.annualRevenueRange
    );
    setSaving(true);
    try {
      await onSave(allAnswered ? { ...input, markComplete: true } : input);
      setStep(returnStep ?? nextStep);
      setReturnStep(null);
    } finally {
      setSaving(false);
    }
  };

  const editStep = (nextStep: number) => {
    setReturnStep((currentReturnStep) => currentReturnStep ?? step);
    setStep(nextStep);
    setChangedSinceEdit(false);
  };

  const getCurrentStepInput = (): UpdateBusinessProfileInput => {
    if (step === 0 && selectedTrade) return { primaryTrade: selectedTrade };
    if (step === 1 && workforce) {
      return {
        workforceStructure: workforce,
        fieldEmployeeCount: fieldCount,
        officeEmployeeCount: officeCount,
      };
    }
    if (step === 2 && stage) return { businessStage: stage };
    if (step === 3 && revenue) return { annualRevenueRange: revenue };
    return {};
  };

  const finishLater = async () => {
    const input = getCurrentStepInput();
    setSaving(true);
    try {
      if (Object.keys(input).length > 0) await onSave(input);
      onFinishLater();
    } finally {
      setSaving(false);
    }
  };

  const confirmNoChanges = async () => {
    let reviewed = false;
    setSaving(true);
    try {
      await onSave({ markReviewed: true });
      reviewed = true;
    } catch (_e) {
      // The check-in was not recorded, so keep the member here with the action still available
      // rather than sending them back to the chat as if the reminder had been dismissed.
    } finally {
      setSaving(false);
    }
    // Navigate last: `onReviewed` unmounts this component, so every state update has to land
    // before it runs.
    if (reviewed) onReviewed();
  };

  const renderStepActions = (onClick: () => void, disabled: boolean) => (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 3,
    }}>
      <Button
        disabled={disabled || saving || (returnStep !== null && !changedSinceEdit)}
        variant="contained"
        onClick={onClick}
        data-testid={TestIds.coach.businessProfile.saveButton}
        sx={{ bgcolor: deepPurple[500], '&:hover': { bgcolor: deepPurple[500] } }}
      >
        {t('coach.businessProfile.actions.save')}
      </Button>
      <Button
        disabled={saving}
        onClick={finishLater}
        data-testid={TestIds.coach.businessProfile.finishLaterButton}
        sx={{
          color: deepPurple[500],
          textDecoration: 'underline',
          '&:hover': { bgcolor: deepPurple[50] },
        }}
      >
        {t('coach.businessProfile.actions.finishLater')}
      </Button>
    </Box>
  );

  const cardSx = {
    border: `${theme.spacing(0.125)} solid ${deepPurple[100]}`,
    borderRadius: theme.spacing(2),
    boxShadow: `0 ${theme.spacing(0.25)} ${theme.spacing(0.5)} rgba(0,0,0,.18)`,
    backgroundColor: theme.palette.common.white,
  };

  if (step === 4) {
    return (
      <Box sx={{
        maxWidth: 1000, width: '100%', alignSelf: 'center', display: 'grid', gap: 2,
      }} data-testid={TestIds.coach.businessProfile.container}>
        {summaries.map((summary) => (
          <Box
            key={summary.step}
            data-testid={`${TestIds.coach.businessProfile.summaryPrefix}-${summary.step}`}
            onClick={() => editStep(summary.step)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') editStep(summary.step); }}
            role="button"
            tabIndex={0}
            sx={{
              ...cardSx, p: 3, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
            }}
          >
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%', bgcolor: deepPurple[50], color: deepPurple[500], display: 'grid', placeItems: 'center',
            }}>{summary.step + 1}</Box>
            <Box sx={{ flex: 1 }}><Typography color="text.secondary">{summary.label}</Typography><Typography fontWeight={700}>{summary.value}</Typography></Box>
            {/* suppress extra tab stop — outer card is role="button" tabIndex={0} */}
            <Button
              component="span"
              tabIndex={-1}
              data-testid={`${TestIds.coach.businessProfile.changePrefix}-${summary.step}`}
              sx={{
                color: deepPurple[500],
                textDecoration: 'underline',
                '&:hover': { backgroundColor: deepPurple[50] },
                pointerEvents: 'none',
              }}
            >{t('coach.businessProfile.actions.change')}</Button>
          </Box>
        ))}
        {/* Lets a member end the quarterly check-in without editing anything, which refreshes
          `updatedAt` and holds the reminder banner off the Coaching page for another quarter.
          Gated on the same three-month check as the banner, so the action only appears while a
          check-in is actually due and disappears alongside the banner once it is taken. */}
        {isBusinessProfileReminderDue(profile) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              disabled={saving}
              onClick={() => confirmNoChanges()}
              data-testid={TestIds.coach.businessProfile.noChangesButton}
              sx={{
                color: deepPurple[500],
                textDecoration: 'underline',
                '&:hover': { bgcolor: deepPurple[50], textDecoration: 'underline' },
              }}
            >
              {t('coach.businessProfile.actions.noChangesNeeded')}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
    }} data-testid={TestIds.coach.businessProfile.container}>
      {returnStep === null && (
        <Box sx={{ textAlign: 'center', maxWidth: theme.spacing(125), width: '100%' }}>
          <Typography variant="h4" data-testid={TestIds.coach.businessProfile.title}>{t('coach.businessProfile.title')}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('coach.businessProfile.description')}
          </Typography>
        </Box>
      )}

      {stepDefs.filter((s) => s.step < step).map((summary) => (
        <Box
          key={summary.step}
          data-testid={`${TestIds.coach.businessProfile.summaryPrefix}-${summary.step}`}
          onClick={() => editStep(summary.step)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') editStep(summary.step); }}
          role="button"
          tabIndex={0}
          sx={{
            ...cardSx, maxWidth: 1000, width: '100%', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%', bgcolor: deepPurple[50], color: deepPurple[500], display: 'grid', placeItems: 'center',
          }}>{summary.step + 1}</Box>
          <Box sx={{ flex: 1 }}><Typography color="text.secondary">{summary.label}</Typography><Typography fontWeight={700}>{summary.value}</Typography></Box>
          {/* suppress extra tab stop — outer card is role="button" tabIndex={0} */}
          <Button
            component="span"
            tabIndex={-1}
            data-testid={`${TestIds.coach.businessProfile.changePrefix}-${summary.step}`}
            sx={{
              color: deepPurple[500],
              textDecoration: 'underline',
              '&:hover': { backgroundColor: deepPurple[50] },
              pointerEvents: 'none',
            }}
          >{t('coach.businessProfile.actions.change')}</Button>
        </Box>
      ))}

      <Box ref={editFormRef} sx={{
        ...cardSx, maxWidth: 1000, width: '100%', p: { xs: 2, md: 4 },
      }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 4,
        }} data-testid={TestIds.coach.businessProfile.progress}>
          {[0, 1, 2, 3].map((value) => (
            <Box
              key={value}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: value <= step ? deepPurple[500] : 'grey.300',
              }}
            />
          ))}
        </Box>

        {step === 0 && <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>{t('coach.businessProfile.trade.heading')}</Typography>
          <Typography variant="body2">{t('coach.businessProfile.trade.label')}</Typography>
          <QuestionComponentAutocomplete
            options={tradePickerOptions}
            inputValue={selectedTradeOption}
            placeholderText={t('coach.businessProfile.trade.selectPlaceholder') ?? ''}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChange={(option) => {
              setSelectedTrade(option && 'value' in option ? option.value : '');
              setChangedSinceEdit(true);
            }}
            onBlur={() => undefined}
            data-testid={TestIds.coach.businessProfile.tradeSelect}
          />
          {renderStepActions(
            () => save({ primaryTrade: selectedTrade }, 1),
            !selectedTrade,
          )}
        </>}

        {step === 1 && <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>{t('coach.businessProfile.workforce.heading')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            {workforceOptions.map((option) => {
              const selected = workforce === option.value;
              return (
                <ChoiceButton
                  key={option.value}
                  testId={`${TestIds.coach.businessProfile.workforceChoicePrefix}-${option.value}`}
                  selected={selected}
                  onClick={() => { setWorkforce(option.value); setChangedSinceEdit(true); }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      bgcolor: selected ?
                        deepPurple[500] : theme.palette.grey[100],
                      color: selected ?
                        theme.palette.common.white : theme.palette.grey[600],
                    }}>
                      {option.icon}
                    </Box>
                    <Box>
                      <Typography fontWeight={700}>{option.title}</Typography>
                      <Typography color={selected ? 'inherit' : 'text.secondary'}>
                        {option.detail}
                      </Typography>
                    </Box>
                  </Box>
                </ChoiceButton>
              );
            })}
          </Box>
          <Box sx={{
            mt: 3, p: 3, bgcolor: deepPurple[50], borderRadius: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3,
          }}>
            {[{
              label: t('coach.businessProfile.workforce.field.label'),
              detail: t('coach.businessProfile.workforce.field.detail'),
              value: fieldCount,
              set: setFieldCount,
              decrementTestId: TestIds.coach.businessProfile.fieldDecrement,
              countTestId: TestIds.coach.businessProfile.fieldCount,
              incrementTestId: TestIds.coach.businessProfile.fieldIncrement,
            }, {
              label: t('coach.businessProfile.workforce.office.label'),
              detail: t('coach.businessProfile.workforce.office.detail'),
              value: officeCount,
              set: setOfficeCount,
              decrementTestId: TestIds.coach.businessProfile.officeDecrement,
              countTestId: TestIds.coach.businessProfile.officeCount,
              incrementTestId: TestIds.coach.businessProfile.officeIncrement,
            }].map((counter) => <Box key={counter.label}><Typography fontWeight={700}>{counter.label}</Typography><Typography color="text.secondary">{counter.detail}</Typography><Box sx={{
              display: 'flex', alignItems: 'center', gap: 2, mt: 1,
            }}><Button data-testid={counter.decrementTestId} variant="outlined" onClick={() => { counter.set(Math.max(0, counter.value - 1)); setChangedSinceEdit(true); }} sx={{ color: deepPurple[500], borderColor: deepPurple[500], '&:hover': { borderColor: deepPurple[500], backgroundColor: deepPurple[50] } }}>−</Button><Typography data-testid={counter.countTestId} fontWeight={700}>{counter.value}</Typography><Button data-testid={counter.incrementTestId} variant="outlined" onClick={() => { counter.set(counter.value + 1); setChangedSinceEdit(true); }} sx={{ color: deepPurple[500], borderColor: deepPurple[500], '&:hover': { borderColor: deepPurple[500], backgroundColor: deepPurple[50] } }}>+</Button></Box></Box>)}
          </Box>
          {renderStepActions(
            () => workforce && save({
              workforceStructure: workforce,
              fieldEmployeeCount: fieldCount,
              officeEmployeeCount: officeCount,
            }, 2),
            !workforce,
          )}
        </>}

        {step === 2 && <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>{t('coach.businessProfile.stage.heading')}</Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {stageOptions.map((option) => {
              const selected = stage === option.value;
              return (
                <ChoiceButton
                  key={option.value}
                  testId={`${TestIds.coach.businessProfile.stageChoicePrefix}-${option.value}`}
                  selected={selected}
                  onClick={() => { setStage(option.value); setChangedSinceEdit(true); }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `${theme.spacing(0.25)} solid ${selected ?
                        deepPurple[500] : theme.palette.grey[300]}`,
                      bgcolor: selected ?
                        deepPurple[500] : 'transparent',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}>
                      {selected && <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: theme.palette.common.white,
                      }} />}
                    </Box>
                    <Typography color={selected ? 'inherit' : 'text.secondary'}>
                      {option.detail}
                    </Typography>
                  </Box>
                </ChoiceButton>
              );
            })}
          </Box>
          {renderStepActions(
            () => stage && save({ businessStage: stage }, 3),
            !stage,
          )}
        </>}

        {step === 3 && <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>{t('coach.businessProfile.revenue.heading')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {revenueOptions.map((option) => <ChoiceButton key={option.value} testId={`${TestIds.coach.businessProfile.revenueChoicePrefix}-${option.value}`} selected={revenue === option.value} onClick={() => { setRevenue(option.value); setChangedSinceEdit(true); }}><Typography width="100%" textAlign="center" fontWeight={700}>{option.label}</Typography></ChoiceButton>)}
          </Box>
          {renderStepActions(
            () => revenue && save({ annualRevenueRange: revenue }, 4),
            !revenue,
          )}
        </>}
      </Box>

      {stepDefs.filter((s) => s.step > step).map((summary) => (
        <Box
          key={summary.step}
          data-testid={`${TestIds.coach.businessProfile.summaryPrefix}-${summary.step}`}
          onClick={() => editStep(summary.step)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') editStep(summary.step); }}
          role="button"
          tabIndex={0}
          sx={{
            ...cardSx, maxWidth: 1000, width: '100%', p: 2.5, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%', bgcolor: deepPurple[50], color: deepPurple[500], display: 'grid', placeItems: 'center',
          }}>{summary.step + 1}</Box>
          <Box sx={{ flex: 1 }}><Typography color="text.secondary">{summary.label}</Typography><Typography fontWeight={700}>{summary.value}</Typography></Box>
          {/* suppress extra tab stop — outer card is role="button" tabIndex={0} */}
          <Button
            component="span"
            tabIndex={-1}
            data-testid={`${TestIds.coach.businessProfile.changePrefix}-${summary.step}`}
            sx={{
              color: deepPurple[500],
              textDecoration: 'underline',
              '&:hover': { backgroundColor: deepPurple[50] },
              pointerEvents: 'none',
            }}
          >{t('coach.businessProfile.actions.change')}</Button>
        </Box>
      ))}
    </Box>
  );
}

export function BusinessProfileBanner({
  profile, onClick,
}: { profile: IBusinessProfile; onClick: () => void }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const complete = getBusinessProfileCompletion(profile);
  // One cadence drives both variants: completeness only decides which one the member sees.
  if (!isBusinessProfileReminderDue(profile)) return null;
  const isComplete = complete === 4;

  return (
    <Box sx={{
      p: 2,
      border: `${theme.spacing(0.125)} solid ${deepPurple[200]}`,
      borderRadius: 1.5,
      bgcolor: deepPurple[50],
      color: deepPurple[500],
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      gap: 2,
    }} data-testid={TestIds.coach.businessProfile.banner}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, flex: 1,
      }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: deepPurple[500], color: theme.palette.common.white,
        }}>
          <BusinessIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: theme.spacing(1.375), display: 'block' }}>
            {isComplete ? t('coach.businessProfile.banner.quarterlyCheckIn') :
              t('coach.businessProfile.banner.percentComplete', { percent: complete * 25 })}
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: theme.spacing(2) }}>
            {isComplete ? t('coach.businessProfile.banner.reviewTitle') :
              t('coach.businessProfile.banner.incompleteTitle')}
          </Typography>
          <Typography sx={{ fontSize: theme.spacing(1.625) }}>
            {isComplete ? t('coach.businessProfile.banner.reviewDescription') :
              t('coach.businessProfile.banner.incompleteDescription')}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        onClick={onClick}
        data-testid={TestIds.coach.businessProfile.bannerAction}
        sx={{
          bgcolor: deepPurple[500],
          alignSelf: { xs: 'flex-end', sm: 'center' },
          '&:hover': { bgcolor: deepPurple[500] },
        }}
      >
        {isComplete ? t('coach.businessProfile.actions.review') :
          t('coach.businessProfile.actions.continue')}
      </Button>
    </Box>
  );
}
